import { CheckIcon } from "@heroicons/react/16/solid";
import { FC, ReactElement } from "react";

export type ProgressBarItem = {
  href?: string;
  id: string;
  name: string;
  status?: "complete" | "current" | "upcoming";
};

export type ProgressBarProps = {
  showStepNumbers?: boolean;
  steps: ProgressBarItem[];
};

export const ProgressBar: FC<ProgressBarProps> = ({
  showStepNumbers,
  steps,
}): ReactElement => {
  return (
    <nav aria-label="Progress">
      <ol
        className="divide-y divide-gray-300 rounded-md border border-gray-300 md:flex md:divide-y-0"
        role="list"
      >
        {steps.map((step, stepIdx) => (
          <li className="relative md:flex md:flex-1" key={step.name}>
            {step.status === "complete" ? (
              <a className="group flex w-full items-center" href={step.href}>
                <span className="flex items-center px-6 py-4 text-sm font-medium">
                  {showStepNumbers && (
                    <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-indigo-600 group-hover:bg-indigo-800 dark:bg-emerald-600 dark:group-hover:bg-emerald-800">
                      <CheckIcon
                        aria-hidden="true"
                        className="size-6 text-white"
                      />
                    </span>
                  )}
                  <span className="ml-4 text-sm font-medium text-gray-900 dark:dark:text-gray-400">
                    {step.name}
                  </span>
                </span>
              </a>
            ) : step.status === "current" ? (
              <a
                aria-current="step"
                className="flex items-center px-6 py-4 text-sm font-medium"
                href={step.href}
              >
                {showStepNumbers && (
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-full border-2 border-indigo-600 dark:border-emerald-600">
                    (
                    <span className="text-indigo-600 dark:text-emerald-600">
                      {step.id}
                    </span>
                  </span>
                )}
                <span className="ml-4 text-sm font-medium text-indigo-600 dark:text-emerald-600">
                  {step.name}
                </span>
              </a>
            ) : (
              <a className="group flex items-center" href={step.href}>
                <span className="flex items-center px-6 py-4 text-sm font-medium">
                  {showStepNumbers && (
                    <span className="flex size-10 shrink-0 items-center justify-center rounded-full border-2 border-gray-300 group-hover:border-gray-400">
                      <span className="text-gray-500 group-hover:text-gray-900 dark:group-hover:text-gray-400">
                        {step.id}
                      </span>
                    </span>
                  )}
                  <span className="ml-4 text-sm font-medium text-gray-500 group-hover:text-gray-900 dark:group-hover:text-gray-400">
                    {step.name}
                  </span>
                </span>
              </a>
            )}

            {stepIdx !== steps.length - 1 ? (
              <>
                {/* Arrow separator for lg screens and up */}
                <div
                  aria-hidden="true"
                  className="absolute right-0 top-0 hidden h-full w-5 md:block"
                >
                  <svg
                    className="size-full text-gray-300 dark:text-gray-400"
                    fill="none"
                    preserveAspectRatio="none"
                    viewBox="0 0 22 80"
                  >
                    <path
                      d="M0 -2L20 40L0 82"
                      stroke="currentcolor"
                      strokeLinejoin="round"
                      vectorEffect="non-scaling-stroke"
                    />
                  </svg>
                </div>
              </>
            ) : null}
          </li>
        ))}
      </ol>
    </nav>
  );
};
